import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  Check,
  ChevronDown,
  FilePenLine,
  Pencil,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { ROUTES } from '#/config/routes';
import { approveStockReturn } from '#/api/imsMgmt/receipts/stockReturn/approve';
import { cancelStockReturn } from '#/api/imsMgmt/receipts/stockReturn/cancel';
import { rejectStockReturn } from '#/api/imsMgmt/receipts/stockReturn/reject';
import { submitStockReturn } from '#/api/imsMgmt/receipts/stockReturn/submit';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  getStockReturnApiErrorMessage,
  isStockReturnVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/apiError';
import {
  canApproveStockReturn,
  canCancelStockReturn,
  canEditStockReturnDraft,
  canEditStockReturnRemark,
  canRejectStockReturn,
  canSubmitStockReturn,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/returnStatus';
import { toast } from '#/utils/toast';
import {
  stockReturnDetailQueryKey,
  stockReturnListQueryKeyPrefix,
} from '#/utils/queryKeys';

const WORKFLOW_EXPOSED_ORDER = ['submit', 'approve', 'reject', 'deleteDraft'];

const ACTION_GROUPS = [
  { key: 'content', label: '單據內容', ids: ['edit', 'editRemark'] },
  {
    key: 'status',
    label: '單據狀態',
    ids: ['submit', 'approve', 'reject', 'deleteDraft'],
  },
];

const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
});

function getExposedActionIds(actionMap) {
  return pickAvailableWorkflowExposedIds(actionMap, WORKFLOW_EXPOSED_ORDER);
}

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function StockReturnExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);

  if (action.to && !disabled) {
    return (
      <Button
        asChild
        variant={action.exposedVariant || 'default'}
        className={cn(action.exposedClassName)}
      >
        <Link to={action.to} aria-label={action.label}>
          {action.showExposedIcon && Icon ? (
            <Icon className="size-3.5" aria-hidden />
          ) : null}
          {action.label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {action.showExposedIcon && Icon ? (
        <Icon className="size-3.5" aria-hidden />
      ) : null}
      {action.label}
    </Button>
  );
}

StockReturnExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function StockReturnMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;

  if (action.to && !disabled) {
    return (
      <DropdownMenuItem
        asChild
        variant={action.destructive ? 'destructive' : 'default'}
      >
        <Link to={action.to}>
          {iconNode}
          {action.label}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled}
      onSelect={() => {
        if (disabled) {
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {action.label}
    </DropdownMenuItem>
  );
}

StockReturnMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function StockReturnActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn('w-auto min-w-44')}>
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <StockReturnMenuItem
                key={action.id}
                action={action}
                isPending={isPending}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

StockReturnActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

const StockReturnCommandBar = ({ detail, onRefetch }) => {
  const canCreate = useCan(PERM.STOCK_RETURN_CREATE);
  const canUpdate = useCan(PERM.STOCK_RETURN_UPDATE);
  const canReview = useCan(PERM.STOCK_RETURN_REVIEW);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: stockReturnListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: stockReturnDetailQueryKey(id) });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return submitStockReturn(id, payload);
      }
      if (action === 'approve') {
        return approveStockReturn(id, payload);
      }
      if (action === 'reject') {
        return rejectStockReturn(id, payload);
      }
      if (action === 'cancel') {
        return cancelStockReturn(id);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      if (variables.action === 'cancel') {
        await queryClient.invalidateQueries({ queryKey: stockReturnListQueryKeyPrefix() });
        navigate(ROUTES.workOrderReturn);
        return;
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getStockReturnApiErrorMessage(error));
      if (isStockReturnVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交退料單',
      message: '提交後將鎖定來源庫存並進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准退料單',
      message: '核准後將移動庫存至目的倉，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }

  async function handleDeleteDraft() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancel' });
  }

  const actionMap = {
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: id ? ROUTES.workOrderReturnEdit(id) : undefined,
      available: canEditStockReturnDraft(status) && canUpdate,
      showExposedIcon: true,
      exposedVariant: 'default',
      exposedClassName: 'document-detail-edit-action',
    },
    editRemark: {
      id: 'editRemark',
      label: '編輯備註',
      icon: FilePenLine,
      to: id ? ROUTES.workOrderReturnRemark(id) : undefined,
      available: canEditStockReturnRemark(status) && canUpdate,
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canSubmitStockReturn(status) && canCreate,
      onSelect: handleSubmit,
    },
    approve: {
      id: 'approve',
      label: '核准',
      icon: Check,
      available: canApproveStockReturn(status) && canReview,
      onSelect: handleApprove,
    },
    reject: {
      id: 'reject',
      label: '退回',
      icon: Undo2,
      available: canRejectStockReturn(status) && canReview,
      destructive: true,
      onSelect: () => setRejectDialogOpen(true),
    },
    deleteDraft: {
      id: 'deleteDraft',
      label: '刪除草稿',
      icon: Trash2,
      available: canCancelStockReturn(status) && canCreate,
      destructive: true,
      onSelect: handleDeleteDraft,
    },
  };

  const mobileLayout = resolveMobileLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
  });
  const mdLayout = resolveMdDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });
  const lgLayout = resolveLgDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <StockReturnExposedActionButton
            key={action.id}
            action={action}
            isPending={isPending}
          />
        ))}
        {layout.showMoreMenu ? (
          <StockReturnActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={isPending}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回退料單"
        description="退回後將恢復為草稿並釋放鎖定，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />

      <div className={cn('flex items-center justify-end gap-2')}>
        <div className={cn('flex md:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mobileLayout, {
            triggerLabel: '操作',
            triggerAriaLabel: '單據操作',
          })}
        </div>
        <div className={cn('hidden md:flex lg:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mdLayout)}
        </div>
        <div className={cn('hidden lg:flex', 'items-center gap-2')}>
          {renderLayoutRow(lgLayout)}
        </div>
      </div>
    </>
  );
};

StockReturnCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    remark: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default StockReturnCommandBar;
